import { Trash2, Mail, ShieldCheck, Clock, Building2, Users } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

/**
 * Public, unauthenticated account & data deletion page.
 *
 * Linked from the in-app Profile page and required by Google Play's data-deletion
 * policy. This same URL must be entered in the Play Console "Data deletion" field.
 *
 * Service Strike accounts are created either by self-service signup on the website or by an
 * organization administrator. A single login can belong to multiple organizations, so deletion is
 * scoped to one organization at a time; the login is fully closed only when the user's
 * last organization membership is removed. The in-app link passes the current
 * organization name via the `org` query param so it can be pre-filled below.
 */

// Address that receives deletion requests. Ensure this mailbox is monitored.
const SUPPORT_EMAIL = 'privacy@servicestrike.com';

export function DeleteAccountPage() {
  const [searchParams] = useSearchParams();
  const orgName = (searchParams.get('org') || '').trim();

  const mailtoHref =
    `mailto:${SUPPORT_EMAIL}` +
    '?subject=' +
    encodeURIComponent('Account & data deletion request') +
    '&body=' +
    encodeURIComponent(
      'I would like to request deletion of my data from a Service Strike organization.\n\n' +
        'Account email: \n' +
        `Organization to remove me from: ${orgName}\n`
    );

  return (
    <div className="h-screen overflow-y-auto bg-secondary-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 sm:px-8 py-6 border-b border-gray-100">
          <div className="rounded-full bg-red-50 p-2.5">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Delete your account &amp; data</h1>
            <p className="text-sm text-gray-500">Service Strike</p>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6 space-y-8 text-gray-700 leading-relaxed">
          <p>
            This page explains how to request deletion of your <strong>Service Strike</strong> data.
            Service Strike accounts are created and managed by organization administrators, so a
            deletion request can be made in either of the ways below.
          </p>

          {/* Per-organization scope */}
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex gap-3">
              <Users className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-amber-900">Deletion is per organization</p>
                <p className="text-sm text-amber-800">
                  Your login can belong to more than one organization. A deletion request removes
                  you from <strong>one organization</strong> and deletes your data within it, so
                  please tell us which organization you mean.
                  {orgName ? (
                    <>
                      {' '}You started this request from{' '}
                      <strong>{orgName}</strong>.
                    </>
                  ) : null}{' '}
                  Your login stays active for any other organizations you belong to, and is fully
                  closed only when your last organization membership is removed.
                </p>
              </div>
            </div>
          </section>

          {/* How to request */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">How to request deletion</h2>

            <div className="flex gap-3">
              <Building2 className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Contact your organization administrator</p>
                <p className="text-sm text-gray-600">
                  Your administrator can deactivate and delete your account directly from the
                  Service Strike management console.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Mail className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Email us</p>
                <p className="text-sm text-gray-600">
                  Send a deletion request from your account email address to{' '}
                  <a href={mailtoHref} className="text-primary-600 underline">
                    {SUPPORT_EMAIL}
                  </a>
                  . We use the sending email address to verify ownership before deleting data.
                </p>
              </div>
            </div>

            <a
              href={mailtoHref}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-white font-medium hover:bg-red-700 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Request deletion by email
            </a>
          </section>

          {/* What gets deleted */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">What data is deleted</h2>
            <p>
              For the organization you are removed from, we delete the data that is not part of that
              organization&apos;s operational or legal records, including:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-600">
              <li>Your membership in and access to that organization</li>
              <li>
                Real-time location history collected while you were working for that organization
              </li>
            </ul>
            <p className="text-sm text-gray-600">
              If this is the last organization on your account, your sign-in login and your
              push-notification and device tokens are removed as well, fully closing your account.
            </p>
          </section>

          {/* What is retained */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">What is retained</h2>
            <p className="text-sm text-gray-600">
              Some data is part of your organization&apos;s operational and legal records and is
              retained even after your account is deleted. This is kept for legitimate business,
              legal, and compliance reasons and, where the law requires, is deleted at the end of
              the applicable retention period.
            </p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-600">
              <li>
                <strong>Your name and role</strong> — retained to preserve accurate attribution in
                job history (which jobs you were assigned to and worked on)
              </li>
              <li>
                <strong>Messages</strong> — internal messages and VoIP/SMS messages you sent or
                received, kept as part of the communication record
              </li>
              <li>
                <strong>Call recordings</strong> — recordings of calls you made or received through
                the app
              </li>
              <li>
                <strong>Job records</strong> — files, photos, and notes attached to the jobs you
                worked on
              </li>
              <li>
                <strong>Financial records</strong> — invoices, payment receipts, and other
                financial or tax records
              </li>
              <li>
                Your <strong>email address and phone number</strong> may be retained where they
                appear within the records above (for example, as part of message or call history)
              </li>
            </ul>
            <div className="flex gap-3 pt-1">
              <ShieldCheck className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">
                Retained records are used only for business, legal, and compliance purposes and are
                not used to re-create your account.
              </p>
            </div>
          </section>

          {/* Timeframe */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">How long it takes</h2>
            <div className="flex gap-3">
              <Clock className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">
                We process verified deletion requests within <strong>30 days</strong>. We will
                confirm by email once your data has been deleted.
              </p>
            </div>
          </section>

          <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            Questions about this process? Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
